import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Contract, IssueType, Issue } from '../interfaces';
import * as constants from '../constants';
import { Modal } from './modal';
import { toHtml } from '../lib/strings';

const ContractIssueList = (props: {
  contract: Contract;
  allIssueTypes: IssueType[];
}) => {
  const [selectedIssue, selectIssue] = useState<null | Issue>(null);
  return (
    <>
      <ContractIssueListContainer className="issue-list">
        {props.allIssueTypes.map(listedIssueType => {
          const activeIssue = props.contract.issues.find(
            issue => issue.type.name === listedIssueType.name
          );

          const onClickIssue = () => {
            if (activeIssue) {
              selectIssue(activeIssue);
            }
          };

          return (
            <li
              className={activeIssue ? 'active' : 'inactive'}
              key={listedIssueType.name}
              onClick={onClickIssue}
            >
              {listedIssueType.name}
            </li>
          );
        })}
      </ContractIssueListContainer>
      <PolicyModal issue={selectedIssue} onClose={() => selectIssue(null)} />
    </>
  );
};

export const PolicyModal = (props: {
  issue: Issue | null;
  onClose: () => void;
}) => {
  const { issue, onClose } = props;

  return (
    <Modal onClose={onClose} show={issue !== null}>
      {issue && (
        <PolicyModalContent>
          <h2>{issue.type.name}</h2>
          {issue.tags && (
            <p className="tags">
              {issue.tags.split(/, */).map(tag => (
                <span className="tag" key={tag}>
                  {tag}
                </span>
              ))}
            </p>
          )}
          <div
            className="description"
            dangerouslySetInnerHTML={{
              __html: toHtml(issue.description),
            }}
          />
        </PolicyModalContent>
      )}
    </Modal>
  );
};

export const ContractIssueListContainer = styled.ol`
  list-style: none;
  font-weight: bold;
  border-bottom: 3px solid ${constants.contentTextColor};
  counter-reset: issue-number;
  padding: 0;
  margin: 0 0 2rem 0;

  > li {
    border-top: 3px solid ${constants.contentTextColor};
    font-size: 16px;
    line-height: 22px;
    position: relative;
    padding: 1.5rem 35px 1.5rem 75px;

    @media (max-width: 767.98px) {
      padding: 1.5em 35px 1.5rem 55px;
      font-size: 15px;
      color: #231f20;
      letter-spacing: 0;
      line-height: 18px;
    }

    &.active {
      cursor: pointer;

      &::after {
        content: '';
        display: block;
        width: 13px;
        height: 13px;
        border-top: 5px solid ${constants.highlightColor};
        border-right: 5px solid ${constants.highlightColor};
        position: absolute;
        right: 15px;
        top: 50%;
        transform: rotate(45deg) translateY(-50%);
      }
    }

    &.inactive {
      color: #dadada;
    }

    &::before {
      counter-increment: issue-number;
      content: counter(issue-number);
      font-size: 40px;
      font-family: VTCMarsha, sans-serif;
      display: inline-block;
      position: absolute;
      left: 20px;
      top: 50%;
      transform: translateY(-50%);

      @media (max-width: 767.98px) {
        font-size: 28px;
      }
    }
  }
`;

export const PolicyModalContent = styled.div`
  h2 {
    font-size: 36px;
    line-height: 42px;
    margin-bottom: 1em;
  }

  .tags {
    margin-bottom: 2.5em;

    .tag {
      display: inline-block;
      margin: 0 0.5em 0.5em 0;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.1);
      padding: 0.5em 1em;
      font-size: 14px;
      color: #333;
    }
  }

  .description {
    p {
      font-size: 18px;
      line-height: 1.333;
      margin-bottom: 1em;
    }
  }
`;

export default ContractIssueList;
